// src/pages/HomePage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  db, 
  doc, 
  getDoc, 
  collection, 
  getDocs, 
  query, 
  where 
} from '../firebase';
import './HomePage.css';

const HomePage = () => {
  const navigate = useNavigate();
  const accountNumber = localStorage.getItem('accountNumber');
  const name = localStorage.getItem('name');
  const [friends, setFriends] = useState([]);
  const [messageStatus, setMessageStatus] = useState({}); // For unread messages (red dot)
  const [storyStatus, setStoryStatus] = useState({}); // For unread stories (green dot)

  // Redirect if not logged in
  useEffect(() => {
    if (!accountNumber) {
      navigate('/');
    }
  }, [accountNumber, navigate]);

  // Fetch friend list with names
  useEffect(() => {
    const fetchFriends = async () => {
      const userRef = doc(db, 'users', accountNumber);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const friendIDs = userSnap.data().friends || [];
        if (friendIDs.length === 0) {
          setFriends([]);
          return;
        }
        const friendsData = await Promise.all(friendIDs.map(async (friendID) => {
          const friendRef = doc(db, 'users', friendID);
          const friendSnap = await getDoc(friendRef);
          return friendSnap.exists() 
            ? { id: friendID, name: friendSnap.data().name || 'Unknown' } 
            : { id: friendID, name: 'Unknown' };
        }));
        setFriends(friendsData);
      } else {
        setFriends([]);
      }
    };

    const fetchUnreadMessages = async () => {
      const messagesRef = collection(db, 'messages');
      const q = query(
        messagesRef, 
        where('receiverID', '==', accountNumber), 
        where('read', '==', false)
      );
      const snapshot = await getDocs(q);
      let status = {};
      snapshot.forEach(doc => {
        const sender = doc.data().senderID;
        status[sender] = true;
      });
      setMessageStatus(status);
    };

    const fetchStoryStatus = async () => {
      let status = {};
      for (let friend of friends) {
        const storyRef = doc(db, 'stories', friend.id);
        const storySnap = await getDoc(storyRef);
        if (storySnap.exists()) {
          const data = storySnap.data();
          // Mark as unread if current user is not in seenBy
          status[friend.id] = !data.seenBy.includes(accountNumber);
        } else {
          status[friend.id] = false;
        }
      }
      setStoryStatus(status);
    };

    fetchFriends();
    fetchUnreadMessages();
    // fetchStoryStatus depends on friends so run it when friends update
  }, [accountNumber]);

  useEffect(() => {
    if (friends.length > 0) {
      const fetchStoryStatus = async () => {
        let status = {};
        for (let friend of friends) {
          const storyRef = doc(db, 'stories', friend.id);
          const storySnap = await getDoc(storyRef);
          if (storySnap.exists()) {
            const data = storySnap.data();
            status[friend.id] = !data.seenBy.includes(accountNumber);
          } else {
            status[friend.id] = false;
          }
        }
        setStoryStatus(status);
      };
      fetchStoryStatus();
    }
  }, [friends, accountNumber]);

  // When a friend container is clicked, navigate to chat
  const handleFriendClick = (friendId) => {
    navigate(`/chat/${friendId}`);
  };

  return (
    <div className="home-container">
      <header className="home-header">
        <h2>Welcome, {name || 'User'}!</h2>
        <p>Your ID: {accountNumber}</p>
      </header>

      <main className="friends-container">
        {friends.length === 0 ? (
          <p>No friends found. Add friends to start chatting!</p>
        ) : (
          friends.map((friend) => (
            <div 
              key={friend.id} 
              className="friend-item" 
              onClick={() => handleFriendClick(friend.id)}
            >
              <span className="friend-name">{friend.name}</span>
              {storyStatus[friend.id] 
                ? <span className="story-dot"></span> 
                : (messageStatus[friend.id] && <span className="unread-dot"></span>)
              }
            </div>
          ))
        )}
      </main>

      <nav className="bottom-nav">
        <button onClick={() => navigate('/home')}>🏠 Home</button>
        <button onClick={() => navigate('/add-friends')}>➕ Add Friends</button>
        <button onClick={() => navigate('/post-story')}>📢 Post Story</button>
      </nav>
    </div>
  );
};

export default HomePage;
